import type { DocMigration } from "../docMigration";

const levelDatumAnchorMigrationMap: Record<string, string> = {
  BOTTOM_OF_FLOOR_PLATE: "LEVEL_BOTTOM_OF_FLOOR_PLATE",
  WINDOW_HEAD_HEIGHT: "LEVEL_WINDOW_HEAD_HEIGHT",
  FLOOR_ELEVATION: "LEVEL_FLOOR_ELEVATION",
  GRADE: "LEVEL_GRADE",
  LEVEL: "LEVEL_FLOOR_ELEVATION",
  CEILING_HEIGHT: "LEVEL_TOP_OF_WALL",
};

const foundationDatumVerticalAnchorMigrationMap: Record<string, string> = {
  LEVEL: "LEVEL_FLOOR_ELEVATION",
  CEILING_HEIGHT: "LEVEL_TOP_OF_WALL",
};

function migrateDatumAnchors(json: any): void {
  if (!json.datums) {
    return;
  }

  for (const datumId in json.datums) {
    const datum = json.datums[datumId];

    // Only LEVEL and FOUNDATION datums have anchor properties
    if (datum.datumType === "LEVEL" || datum.datumType === "FOUNDATION") {
      const anchor = datum.anchor;
      if (!anchor) {
        continue;
      }

      // Migrate LevelDatumAnchor values
      if (levelDatumAnchorMigrationMap[anchor]) {
        datum.anchor = levelDatumAnchorMigrationMap[anchor];
      }
      // Migrate FoundationDatumVerticalAnchor values
      else if (foundationDatumVerticalAnchorMigrationMap[anchor]) {
        datum.anchor = foundationDatumVerticalAnchorMigrationMap[anchor];
      }
    }
  }
}

function migrateFoundationSettings(json: any): void {
  if (!json.settings) {
    return;
  }

  for (const settingsId in json.settings) {
    const settings = json.settings[settingsId];
    if (!settings || settings.type !== "FOUNDATION_SETTINGS") {
      continue;
    }

    // Migrate foundation hash settings
    for (const foundationHash in settings) {
      if (foundationHash === "type" || foundationHash === "foundationHash") {
        continue;
      }

      const foundationSetting = settings[foundationHash];
      if (!foundationSetting) {
        continue;
      }

      const fieldsToMigrate = [
        "garageDatum",
        "porchDatum",
        "deckDatum",
        "brickLedgeDatum",
        "foundationDatum",
        "basementFoundationDatum",
      ] as const;

      for (const field of fieldsToMigrate) {
        const value = foundationSetting[field];
        if (value && levelDatumAnchorMigrationMap[value]) {
          foundationSetting[field] = levelDatumAnchorMigrationMap[value];
        }
      }
    }
  }
}

function migrateWainscotingSettings(json: any): void {
  if (!json.settings) {
    return;
  }

  for (const settingsId in json.settings) {
    const settings = json.settings[settingsId];
    if (!settings || settings.type !== "WAINSCOTING_SETTINGS") {
      continue;
    }

    // Migrate waincotingDatum (LevelDatumAnchor)
    const waincotingDatum = settings.waincotingDatum;
    if (waincotingDatum && levelDatumAnchorMigrationMap[waincotingDatum]) {
      settings.waincotingDatum = levelDatumAnchorMigrationMap[waincotingDatum];
    }

    // Migrate porchWainscotingDatum (FoundationDatumVerticalAnchor)
    const porchWainscotingDatum = settings.porchWainscotingDatum;
    if (porchWainscotingDatum && foundationDatumVerticalAnchorMigrationMap[porchWainscotingDatum]) {
      settings.porchWainscotingDatum = foundationDatumVerticalAnchorMigrationMap[porchWainscotingDatum];
    }
  }
}

function migrateFloorAssemblies(json: any): void {
  if (!json.floors) {
    return;
  }

  for (const floorId in json.floors) {
    const floor = json.floors[floorId];
    if (floor.assemblyType !== "SOLID") {
      continue;
    }
    const topDatum = floor.topDatum;
    if (topDatum && foundationDatumVerticalAnchorMigrationMap[topDatum]) {
      floor.topDatum = foundationDatumVerticalAnchorMigrationMap[topDatum];
    }
  }
}

// eslint-disable-next-line @typescript-eslint/naming-convention
const migration_0_0_507: DocMigration = {
  maxSchemaVersion: "0.0.507",
  description: "Migrate LevelDatumAnchor and FoundationDatumVerticalAnchor enum values",
  apply(json: any): any {
    migrateDatumAnchors(json);
    migrateFoundationSettings(json);
    migrateWainscotingSettings(json);
    migrateFloorAssemblies(json);

    return json;
  },
};

export default migration_0_0_507;
